import { createSelector, createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';
import buildQuery from 'odata-query';
import { RootState } from '../..';
import { createReport, fetchReports, canAccessReport } from './reportingAPI';
import {
    IPagination,
    ISorting,
    LoadStatus,
    ODataResult,
    ReportModel,
    ReportParametersInputModel,
} from '../../../model';
import { convertToOdataOptions } from '../../../utils/odata';
import { createAppThunk } from '../../thunks';
import { ReportTypeRequest } from '../../../model/ReportTypeRequest';

export interface ReportingBoardState {
    items: ReportModel[];
    pagination: IPagination;
    sorting: ISorting;
    nextPageToken: string | null;
    loadStatus: LoadStatus;
    lastReportCreationFailure?: number;
    lastReportCreationSuccess?: number;
    canAccessGroundTeamRatingsReport: boolean | null;
    canAccessGroundTeamRatingsReportStatus: LoadStatus;
    canViewAllGroundTeamRatingsReport: boolean | null;
    canViewAllGroundTeamRatingsReportStatus: LoadStatus;
}

const initialState: ReportingBoardState = {
    items: [],
    pagination: {
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
    },
    sorting: {
        sortBy: 'createdTimeUtc',
        sortByDescending: true,
    },
    loadStatus: 'idle',
    nextPageToken: null,
    canAccessGroundTeamRatingsReport: null,
    canAccessGroundTeamRatingsReportStatus: 'idle',
    canViewAllGroundTeamRatingsReport: null,
    canViewAllGroundTeamRatingsReportStatus: 'idle',
};

const getReportQuery = (state: RootState) => {
    const { reportingBoard } = state;

    const options = convertToOdataOptions<ReportModel>({
        count:
            reportingBoard.pagination.currentPage === 1 &&
            reportingBoard.pagination.pageSize > 0,
        pagination: reportingBoard.pagination,
        sorting: reportingBoard.sorting,
    });

    options.select = [
        'id',
        'aadUserId',
        'fileName',
        'type',
        'parameters',
        'status',
        'createdTimeUtc',
    ];

    const query = buildQuery(options);

    return query;
};

export const loadReports = createAppThunk<ODataResult<ReportModel[]>>(
    'reportingBoard/loadReports',
    async (_, { getState }) => {
        const query = getReportQuery(getState());
        return await fetchReports(query);
    }
);

export const fetchCanAccessGroundTeamRatingsReport = createAppThunk<boolean>(
    'reportingBoard/fetchCanAccessGroundTeamRatingsReport',
    async () => {
        const res = await canAccessReport({
            reportType: ReportTypeRequest.GroundConversationReport,
            allTeams: false,
        });

        return res.data;
    }
);

export const fetchCanViewAllGroundTeamRatingsReport = createAppThunk<boolean>(
    'reportingBoard/fetchCanViewAllGroundTeamRatingsReport',
    async () => {
        const res = await canAccessReport({
            reportType: ReportTypeRequest.GroundConversationReport,
            allTeams: true,
        });

        return res.data;
    }
);

export const createNewReport = createAppThunk<
    boolean,
    ReportParametersInputModel
>(
    'reportingBoard/createNewReport',
    async (data: ReportParametersInputModel) => {
        return await createReport(data);
    }
);

export const reportingBoardSlice: Slice<ReportingBoardState> = createSlice({
    name: 'reportingBoard',
    initialState,
    reducers: {
        toggleSort: (state, action: PayloadAction<string>) => {
            if (state.sorting.sortBy === action.payload) {
                state.sorting.sortByDescending =
                    !state.sorting.sortByDescending;
            } else {
                state.sorting.sortBy = action.payload;
                state.sorting.sortByDescending = false;
            }
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage =
                action.payload < 1 ? 1 : action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadReports.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(loadReports.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(loadReports.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload.value;
                if (!!action.payload['@odata.count']) {
                    state.pagination.totalCount =
                        action.payload['@odata.count'];
                }
            })
            .addCase(createNewReport.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(createNewReport.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(createNewReport.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                const success = action.payload;

                if (!success) {
                    state.lastReportCreationFailure = new Date().valueOf();
                }
            })
            .addCase(fetchCanAccessGroundTeamRatingsReport.pending, (state) => {
                state.canAccessGroundTeamRatingsReportStatus = 'loading';
            })
            .addCase(
                fetchCanAccessGroundTeamRatingsReport.rejected,
                (state) => {
                    state.canAccessGroundTeamRatingsReportStatus = 'failed';
                    state.canAccessGroundTeamRatingsReport = false;
                }
            )
            .addCase(
                fetchCanAccessGroundTeamRatingsReport.fulfilled,
                (state, action) => {
                    state.canAccessGroundTeamRatingsReportStatus = 'idle';
                    state.canAccessGroundTeamRatingsReport = action.payload;
                }
            )
            .addCase(
                fetchCanViewAllGroundTeamRatingsReport.pending,
                (state) => {
                    state.canViewAllGroundTeamRatingsReportStatus = 'loading';
                }
            )
            .addCase(
                fetchCanViewAllGroundTeamRatingsReport.rejected,
                (state) => {
                    state.canViewAllGroundTeamRatingsReportStatus = 'failed';
                    state.canViewAllGroundTeamRatingsReport = false;
                }
            )
            .addCase(
                fetchCanViewAllGroundTeamRatingsReport.fulfilled,
                (state, action) => {
                    state.canViewAllGroundTeamRatingsReportStatus = 'idle';
                    state.canViewAllGroundTeamRatingsReport = action.payload;
                }
            );
    },
});

export const { toggleSort, setCurrentPage } = reportingBoardSlice.actions;
export const selectReportingBoard = (state: RootState) => state.reportingBoard;

export const selectCanAccessGroundTeamRatingsReport = createSelector(
    [(state: RootState) => state.reportingBoard],
    (reportingBoard) => ({
        canAccess: reportingBoard.canAccessGroundTeamRatingsReport,
        status: reportingBoard.canAccessGroundTeamRatingsReportStatus,
    })
);

export const selectCanViewAllGroundTeamRatingsReport = createSelector(
    [(state: RootState) => state.reportingBoard],
    (reportingBoard) => ({
        canViewAllTeams: reportingBoard.canViewAllGroundTeamRatingsReport,
        status: reportingBoard.canViewAllGroundTeamRatingsReportStatus,
    })
);

export default reportingBoardSlice.reducer;
